import { useCallback, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

const BASE = '/api/v1/memberspot-training';

export default function useMemberspotTraining() {
  const authCtx = useContext(AuthContext);
  const { sendRequest, isLoading, error, setError } = useHttp();

  const buildHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', ...authCtx.getAuthHeaders() }),
    [authCtx]
  );

  const safe = useCallback(async (request, errorMessage) => {
    try {
      return await request();
    } catch (err) {
      toast.error(errorMessage || 'Fehler beim Memberspot-Schulungssystem');
      throw err;
    }
  }, []);

  const listOffers = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/offers`,
            method: 'GET',
            headers: buildHeaders(),
          }),
        'Fehler beim Laden der Memberspot Angebote'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const syncOffers = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/offers/sync`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Synchronisieren der Memberspot Angebote'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const updateOfferMetadata = useCallback(
    (offerId, payload) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/offers/${offerId}/metadata`,
            method: 'PATCH',
            headers: buildHeaders(),
            body: payload,
          }),
        'Fehler beim Speichern der Memberspot Angebotsdetails'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const listRules = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules`,
            method: 'GET',
            headers: buildHeaders(),
          }),
        'Fehler beim Laden der Schulungsregeln'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const createRule = useCallback(
    payload =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules`,
            method: 'POST',
            headers: buildHeaders(),
            body: payload,
          }),
        'Fehler beim Erstellen der Schulungsregel'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const updateRule = useCallback(
    (ruleId, payload) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules/${ruleId}`,
            method: 'PUT',
            headers: buildHeaders(),
            body: payload,
          }),
        'Fehler beim Speichern der Schulungsregel'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const deleteRule = useCallback(
    ruleId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules/${ruleId}`,
            method: 'DELETE',
            headers: buildHeaders(),
          }),
        'Fehler beim Deaktivieren der Schulungsregel'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const previewBackfill = useCallback(
    ruleId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules/${ruleId}/backfill-preview`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Vorbereiten der Schulungs-Backfill-Vorschau'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const executeBackfill = useCallback(
    ruleId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules/${ruleId}/backfill`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Backfill der Schulungsregel'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const listRuleAssignments = useCallback(
    ruleId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/rules/${ruleId}/assignments`,
            method: 'GET',
            headers: buildHeaders(),
          }),
        'Fehler beim Laden der Regel-Zuweisungen'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const listAssignments = useCallback(
    (filters = {}) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments`,
            method: 'GET',
            headers: buildHeaders(),
            params: filters,
          }),
        'Fehler beim Laden der Schulungszuweisungen'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const syncAssignment = useCallback(
    assignmentId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments/${assignmentId}/sync`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Synchronisieren der Schulungszuweisung'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const resendAssignmentInvitation = useCallback(
    assignmentId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments/${assignmentId}/resend`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim erneuten Senden der Einladung'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const acknowledgeConversation = useCallback(
    (assignmentId, note) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments/${assignmentId}/acknowledge-conversation`,
            method: 'POST',
            headers: buildHeaders(),
            body: { note: note || '' },
          }),
        'Fehler beim Bestätigen des Mitarbeitergesprächs'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const openMemberspotLink = useCallback(
    assignmentId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments/${assignmentId}/open-link`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Erzeugen des Memberspot Login-Links'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const openMyMemberspotLink = useCallback(
    assignmentId =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/me/assignments/${assignmentId}/open-link`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Erzeugen des Memberspot Login-Links'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const runConsequence = useCallback(
    (assignmentId, action) =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/assignments/${assignmentId}/consequence`,
            method: 'POST',
            headers: buildHeaders(),
            body: { action },
          }),
        'Fehler beim Ausführen der Konsequenz'
      ),
    [buildHeaders, safe, sendRequest]
  );

  // Intentionally not wrapped in `safe`: this is fetched on /welcome by
  // an optional dashboard card that should silently render nothing if
  // the request fails. The dedicated /myTrainings view handles its own
  // empty state too, so a toast would only be noisy.
  const getMyAssignments = useCallback(
    () =>
      sendRequest({
        url: `${BASE}/me`,
        method: 'GET',
        headers: buildHeaders(),
      }),
    [buildHeaders, sendRequest]
  );

  const getMcpStatus = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mcp/status`,
            method: 'GET',
            headers: buildHeaders(),
          }),
        'Fehler beim Laden des Memberspot-MCP-Status'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const connectMcp = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mcp/connect`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Vorbereiten der Memberspot-MCP-Verbindung'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const disconnectMcp = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mcp/disconnect`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Trennen der Memberspot-MCP-Verbindung'
      ),
    [buildHeaders, safe, sendRequest]
  );

  const syncMcpCourseNames = useCallback(
    () =>
      safe(
        () =>
          sendRequest({
            url: `${BASE}/mcp/sync-course-names`,
            method: 'POST',
            headers: buildHeaders(),
          }),
        'Fehler beim Synchronisieren der Kursnamen aus Memberspot'
      ),
    [buildHeaders, safe, sendRequest]
  );

  return useMemo(
    () => ({
      isLoading,
      error,
      setError,
      listOffers,
      syncOffers,
      updateOfferMetadata,
      listRules,
      createRule,
      updateRule,
      deleteRule,
      previewBackfill,
      executeBackfill,
      listRuleAssignments,
      listAssignments,
      syncAssignment,
      resendAssignmentInvitation,
      acknowledgeConversation,
      openMemberspotLink,
      openMyMemberspotLink,
      runConsequence,
      getMyAssignments,
      getMcpStatus,
      connectMcp,
      disconnectMcp,
      syncMcpCourseNames,
    }),
    [
      isLoading,
      error,
      setError,
      listOffers,
      syncOffers,
      updateOfferMetadata,
      listRules,
      createRule,
      updateRule,
      deleteRule,
      previewBackfill,
      executeBackfill,
      listRuleAssignments,
      listAssignments,
      syncAssignment,
      resendAssignmentInvitation,
      acknowledgeConversation,
      openMemberspotLink,
      openMyMemberspotLink,
      runConsequence,
      getMyAssignments,
      getMcpStatus,
      connectMcp,
      disconnectMcp,
      syncMcpCourseNames,
    ]
  );
}
